import {
  readAccessPinStatus,
  type AccessPinStatusItem,
  type PinLookup,
} from '@/api/core/access-pin-status';
import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import { devicePurposes } from '@/api/core/device-purpose';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import { formatDateTimeWithOffset } from '@/lib/format';
import { Badge, Button } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { PropertyRuntime } from '../property/property-queries';

/** 조회 결과 사유. PIN 설정 여부를 모를 때 왜 모르는지를 그대로 알린다. */
const LOOKUP_REASON: Record<PinLookup, string> = {
  OBSERVED: '장비가 응답했습니다.',
  ABSENT: '장비에 이 예약 번호의 키가 없습니다.',
  MISMATCH: '장비가 다른 예약·방 정보를 응답해 확인하지 못했습니다.',
  UNAVAILABLE: '장비와 통신하지 못해 확인하지 못했습니다.',
  NOT_CONFIGURED: '이 환경에서는 실제 장비 조회를 사용하지 않아 확인하지 않았습니다.',
  NO_RESERVATION: '조회할 예약 번호의 근거가 없어 확인하지 않았습니다.',
};
const SOURCE_LABEL = {
  CORE_RESERVATION: 'Core 예약 번호',
  JAVA_SLOT: '기존(Java) 슬롯 번호',
} as const;

function pinSummary(item: AccessPinStatusItem) {
  if (item.pin_configured === true) return { text: 'PIN 설정됨', tone: 'success' as const };
  if (item.pin_state === 'trying to set') return { text: 'PIN 설정 중', tone: 'warning' as const };
  if (item.pin_configured === false) return { text: 'PIN 없음', tone: 'neutral' as const };
  return { text: 'PIN 설정 여부 확인 불가', tone: 'warning' as const };
}

function Item({ item }: { item: AccessPinStatusItem }) {
  const summary = pinSummary(item);
  return (
    <li className="space-y-1 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">
          {item.device_id}
          {item.gateway_id ? ` · 게이트웨이 ${item.gateway_id}` : ''}
        </span>
        <Badge tone="neutral">{devicePurposes[item.purpose]}</Badge>
        <Badge tone={summary.tone}>{summary.text}</Badge>
        {item.multi_pin === true && <Badge tone="info">멀티핀</Badge>}
      </div>
      <p className="body-sm text-muted-foreground">{LOOKUP_REASON[item.lookup]}</p>
      <dl className="body-sm grid gap-x-4 gap-y-1 sm:grid-cols-[8rem_minmax(0,1fr)]">
        <dt className="text-muted-foreground">핀 모드</dt>
        <dd>
          {item.pin_mode ?? '확인되지 않음'}
          {item.multi_pin === null && item.pin_mode !== null && (
            <span className="text-muted-foreground"> · 멀티핀 여부는 확인되지 않았습니다.</span>
          )}
        </dd>
        <dt className="text-muted-foreground">예약 번호 근거</dt>
        <dd>{item.reservation_source ? SOURCE_LABEL[item.reservation_source] : '해당 없음'}</dd>
      </dl>
    </li>
  );
}

/**
 * 슬롯(Unit) 카드의 PIN 설정 여부·멀티핀 여부 조회. 서버가 연결된 LoRa 도어락 전부를 장비에서 실시간으로 읽고
 * 응답 시간 제한을 두지 않으므로, 슬롯마다 자동으로 부르지 않고 운영자가 눌렀을 때만 읽는다.
 * PIN 원문은 오지 않으며 이 화면은 어떤 값도 쓰지 않는다.
 */
export function SlotPinStatus({
  runtime,
  unitId,
  label,
}: {
  runtime: PropertyRuntime;
  unitId: string;
  /** 접근 가능한 이름용 슬롯 표시(예: '101호 · 침대 A'). */
  label: string;
}) {
  const session = useOperatorSession(runtime.session);
  const [requested, setRequested] = useState(false);
  const authorization = session.authorization;
  const allowed =
    session.status === 'authorized' &&
    !session.deniedDomains.includes('entitlement') &&
    !!authorization &&
    canReadDomain(authorization, 'entitlement', 1);
  const generation = session.generation;
  const query = useQuery({
    queryKey: [...coreQueryKey(runtime.apiNamespace, generation, 'access-pin-status'), unitId],
    queryFn: async ({ signal }) => {
      try {
        return await readAccessPinStatus(runtime.client, unitId, signal);
      } catch (error) {
        if (
          !signal.aborted &&
          isApiError(error) &&
          error.status === 403 &&
          runtime.session.getSnapshot().generation === generation
        )
          runtime.session.reportDomainAccessDenied('entitlement', generation);
        throw error;
      }
    },
    enabled: requested && allowed,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    meta: { suppressGlobalError: true },
  });

  if (!allowed)
    return (
      <p className="body-sm text-muted-foreground">
        PIN 설정 여부 조회에는 출입 자격 조회 권한이 필요합니다.
      </p>
    );
  return (
    <section aria-label={`${label} PIN 설정 상태`} className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={query.isFetching}
          onClick={() => {
            if (requested) void query.refetch();
            else setRequested(true);
          }}
        >
          {requested ? 'PIN 설정 상태 다시 확인' : 'PIN 설정 상태 확인'}
        </Button>
        <span className="caption text-muted-foreground">
          장비에서 실시간으로 읽습니다. PIN 값은 표시하지 않습니다.
        </span>
      </div>
      {query.isFetching && (
        <p role="status">
          장비 응답을 기다리고 있습니다. 응답이 올 때까지 기다리며, 늦을 수 있습니다.
        </p>
      )}
      {query.isError && !query.isFetching && (
        <p role="alert">
          {isApiError(query.error) && query.error.status === 404
            ? '슬롯을 찾을 수 없습니다.'
            : isApiError(query.error) && query.error.status === 503
              ? '연결된 도어락이 너무 많거나 서비스를 사용할 수 없어 조회하지 못했습니다.'
              : 'PIN 설정 상태를 확인하지 못했습니다. 다시 확인해 주세요.'}
        </p>
      )}
      {query.isSuccess && !query.isFetching && (
        <>
          <p className="caption text-muted-foreground">
            확인 시각 {formatDateTimeWithOffset(query.data.observed_at)}
          </p>
          {query.data.items.length === 0 ? (
            <p>이 슬롯에 연결된 LoRa 도어락이 없습니다.</p>
          ) : (
            <ul aria-label={`${label} 도어락 PIN 상태`} className="space-y-2">
              {query.data.items.map((item) => (
                <Item key={item.core_device_id} item={item} />
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
